import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Calendar as CalIcon, Check, X, RefreshCw, ChevronLeft, ChevronRight, Plus, Settings as SettingsIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BOOKABLE_GROUPS, parsePrice, formatEUR } from "@/data/site";
import { StaffTab } from "@/components/kalender/StaffTab";
import { ResourcesTab } from "@/components/kalender/ResourcesTab";
import { CustomersTab } from "@/components/kalender/CustomersTab";
import { WaitlistTab } from "@/components/kalender/WaitlistTab";
import { AcademyTab } from "@/components/kalender/AcademyTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: "Offen", cls: "bg-muted text-foreground/70" },
  accepted: { label: "Bestätigt", cls: "bg-primary text-primary-foreground" },
  rejected: { label: "Abgelehnt", cls: "bg-foreground/10 text-foreground/50 line-through" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const WEEK_HOURS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeekMonday = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Time grid: 09:00 – 18:00 in 30-minute rows (18 rows total)
const HOUR_START = 9;
const HOUR_END = 18;
const ROWS_PER_HOUR = 2; // 30-minute precision
const ROW_HEIGHT = 32;   // px per 30 min
const TOTAL_ROWS = (HOUR_END - HOUR_START) * ROWS_PER_HOUR;

const timeToRow = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h - HOUR_START) * ROWS_PER_HOUR + Math.floor(m / (60 / ROWS_PER_HOUR));
};
const timeToMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + m;
};
const rowToTime = (row) => {
  const totalMin = HOUR_START * 60 + row * (60 / ROWS_PER_HOUR);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const STATUS_STYLE = {
  pending:  "bg-amber-100 border-l-4 border-amber-500 text-amber-950",
  accepted: "bg-primary/15 border-l-4 border-primary text-primary",
  rejected: "bg-foreground/5 border-l-4 border-foreground/30 text-foreground/50 line-through",
};

// Category → Farbe für bestätigte Termine
const categoryStyle = (service = "") => {
  const s = service.toLowerCase();
  const hasM = s.includes("manik");
  const hasP = s.includes("pedik");
  if (hasM && hasP) return "bg-[#E6D8B8]/70 border-l-4 border-[#8C7F72] text-[#3A2F25]";   // beige
  if (hasP)         return "bg-[#7A5B3B]/12 border-l-4 border-[#7A5B3B] text-[#5A3E20]";  // braun
  if (hasM)         return "bg-primary/15 border-l-4 border-primary text-primary";       // grün
  return "bg-primary/10 border-l-4 border-primary/60 text-primary";
};

const chipStyle = (b) => {
  if (b.status === "rejected") return STATUS_STYLE.rejected;
  if (b.status === "pending")  return STATUS_STYLE.pending;
  return categoryStyle(b.service);
};

const WeekView = ({ anchorDate, setAnchorDate, dayRange, setDayRange, staff = [], bookings, closures = [], onEmptySlot, onBookingClick, onReschedule }) => {
  // If dayRange === 5 -> Mon-Fri of the week containing anchorDate. Else -> anchorDate + next (range-1) days.
  const startDate = dayRange === 5 ? startOfWeekMonday(anchorDate) : new Date(anchorDate);
  const days = Array.from({ length: dayRange }, (_, i) => addDays(startDate, i));
  const label = dayRange === 1
    ? days[0].toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : `${days[0].toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${days[days.length-1].toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
  const [dragOver, setDragOver] = useState(null); // "iso_staff_row"
  const [dragging, setDragging] = useState(null);
  const today = isoDate(new Date());

  // Columns: if we have >=1 active staff, one column per staff per day; else fallback single column per day
  const cols = staff.length > 0 ? staff : [{ id: "__none__", name: "Termine", color: "#6A7B62" }];
  const totalCols = days.length * cols.length;

  const closureFor = (iso) => (closures || []).find((c) => (c.start || "") <= iso && iso <= (c.end || c.start || ""));
  const stepDays = dayRange;

  // Bookings grouped by date -> array
  const byDate = bookings.reduce((acc, b) => {
    if (b.status === "rejected") return acc;
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});

  const bookingCol = (b) => {
    if (staff.length === 0) return 0;
    const idx = cols.findIndex((c) => c.id === b.staff_id);
    // fallback to first column if no staff assigned or staff no longer active
    return idx >= 0 ? idx : 0;
  };

  return (
    <div data-testid="kalender-week" className="mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <button data-testid="week-prev" onClick={() => setAnchorDate(addDays(startDate, -stepDays))} className="w-10 h-10 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"><ChevronLeft size={18} /></button>
        <div className="text-center flex-1">
          <div className="font-serif text-2xl">{label}</div>
          <button data-testid="week-today" onClick={() => setAnchorDate(new Date())} className="text-xs uppercase tracking-[0.2em] text-primary mt-1">Heute</button>
        </div>
        <div className="flex border border-border" data-testid="range-picker">
          {[
            { r: 1, label: "1 Tag" },
            { r: 3, label: "3 Tage" },
            { r: 5, label: "Woche" },
          ].map((o) => (
            <button key={o.r} data-testid={`range-${o.r}`}
              onClick={() => {
                setDayRange(o.r);
                if (o.r === 5) setAnchorDate(startOfWeekMonday(anchorDate));
              }}
              className={`px-3 py-2 text-xs uppercase tracking-widest transition-colors ${dayRange === o.r ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"}`}>{o.label}</button>
          ))}
        </div>
        <button data-testid="week-next" onClick={() => setAnchorDate(addDays(startDate, stepDays))} className="w-10 h-10 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"><ChevronRight size={18} /></button>
      </div>

      <div className="overflow-x-auto border border-border bg-background">
        <div style={{ minWidth: 100 + totalCols * (cols.length > 1 ? 140 : 160), display: "grid", gridTemplateColumns: `70px repeat(${totalCols}, minmax(0,1fr))` }}>
          {/* Header row 1: day labels spanning staff columns */}
          <div className="bg-muted border-b border-r border-border" />
          {days.map((d) => {
            const iso = isoDate(d);
            const active = iso === today;
            const cl = closureFor(iso);
            return (
              <div key={`h-${iso}`} className={`p-2 text-center border-b border-r border-border ${cl ? "bg-foreground/8 text-foreground/50" : active ? "bg-primary text-primary-foreground" : "bg-muted"}`} style={{ gridColumn: `span ${cols.length}` }} title={cl ? `Studio geschlossen: ${cl.note || ""}` : ""}>
                <div className="text-[10px] uppercase tracking-[0.2em]">{d.toLocaleDateString("de-DE", { weekday: dayRange === 1 ? "long" : "short" })}</div>
                <div className="font-serif text-xl mt-0.5">{d.getDate()}.{String(d.getMonth() + 1).padStart(2, "0")}</div>
                {cl && <div className="text-[9px] uppercase tracking-widest mt-1 opacity-80">Geschlossen</div>}
              </div>
            );
          })}

          {/* Header row 2: staff sub-columns (only shown if multiple) */}
          {cols.length > 1 && <>
            <div className="bg-muted/50 border-b border-r border-border" />
            {days.flatMap((d) => cols.map((s) => (
              <div key={`sub-${isoDate(d)}-${s.id}`} className="border-b border-r border-border text-[10px] uppercase tracking-widest text-center py-1.5 flex items-center justify-center gap-1.5" style={{ background: `${s.color}18`, color: s.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                {s.name.split(" ")[0]}
              </div>
            )))}
          </>}

          {/* Time axis */}
          <div className="border-r border-border relative bg-muted/30" style={{ height: TOTAL_ROWS * ROW_HEIGHT }}>
            {Array.from({ length: HOUR_END - HOUR_START }).map((_, i) => (
              <div key={i} className="absolute left-0 right-0 border-t border-border/50 pl-2 pt-1 text-[10px] tracking-widest text-muted-foreground" style={{ top: i * ROWS_PER_HOUR * ROW_HEIGHT, height: ROWS_PER_HOUR * ROW_HEIGHT }}>
                {String(HOUR_START + i).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {/* Day × Staff columns */}
          {days.flatMap((d) => {
            const iso = isoDate(d);
            const dayBookings = (byDate[iso] || []).sort((a, b) => a.time.localeCompare(b.time));
            const cl = closureFor(iso);
            return cols.map((s, colIdx) => {
              const staffId = s.id === "__none__" ? null : s.id;
              const staffBookings = dayBookings.filter((b) => {
                if (cols.length === 1) return true;
                if (!b.staff_id && colIdx === 0) return true; // unassigned goes to first column
                return b.staff_id === staffId;
              });
              return (
                <div key={`${iso}-${s.id}`} className={`relative border-r border-border ${cl ? "bg-foreground/5" : ""}`} style={{ height: TOTAL_ROWS * ROW_HEIGHT }}>
                  {cl && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-[10px] uppercase tracking-[0.25em] text-foreground/40" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 12px, rgba(0,0,0,0.05) 12px 24px)" }}>
                      <span className="bg-background/80 px-2 py-1 border border-border">Geschlossen</span>
                    </div>
                  )}
                  {/* clickable + drop slots */}
                  {Array.from({ length: TOTAL_ROWS }).map((_, row) => {
                    const overKey = `${iso}_${s.id}_${row}`;
                    const isOver = dragOver === overKey;
                    return (
                      <button
                        key={row}
                        type="button"
                        data-testid={`slot-${iso}-${s.id}-${row}`}
                        onClick={() => onEmptySlot(iso, rowToTime(row), staffId)}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(overKey); }}
                        onDragLeave={() => setDragOver((k) => (k === overKey ? null : k))}
                        onDrop={(e) => {
                          e.preventDefault();
                          const id = e.dataTransfer.getData("text/plain");
                          setDragOver(null);
                          setDragging(null);
                          if (id) onReschedule(id, iso, rowToTime(row), staffId);
                        }}
                        className={`absolute left-0 right-0 border-t transition-colors ${row % ROWS_PER_HOUR === 0 ? "border-border/50" : "border-transparent"} ${isOver ? "bg-primary/25" : "hover:bg-primary/5"}`}
                        style={{ top: row * ROW_HEIGHT, height: ROW_HEIGHT }}
                        aria-label={`Neuer Termin ${rowToTime(row)} – ${s.name}`}
                      />
                    );
                  })}
                  {/* Booking chips */}
                  {staffBookings.map((b) => {
                    const startRow = timeToRow(b.time || "09:00");
                    const heightRows = Math.max(1, Math.round((b.duration_minutes || 60) / (60 / ROWS_PER_HOUR)));
                    const top = startRow * ROW_HEIGHT + 2;
                    const height = heightRows * ROW_HEIGHT - 4;
                    const baseSt = chipStyle(b);
                    const isDragging = dragging === b.id;
                    // If we have real staff, override left-border color to staff color
                    const staffColor = staffId ? s.color : null;
                    return (
                      <div
                        key={b.id}
                        data-testid={`chip-${b.id}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", b.id);
                          e.dataTransfer.effectAllowed = "move";
                          setDragging(b.id);
                        }}
                        onDragEnd={() => { setDragging(null); setDragOver(null); }}
                        onClick={(e) => { e.stopPropagation(); onBookingClick(b); }}
                        role="button"
                        tabIndex={0}
                        className={`absolute left-1 right-1 rounded-sm px-2 py-1.5 text-left overflow-hidden hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${baseSt} ${isDragging ? "opacity-40" : ""}`}
                        style={{ top, height, ...(staffColor ? { borderLeftColor: staffColor, borderLeftWidth: 4, borderLeftStyle: "solid" } : {}) }}
                        title="Ziehen zum Verschieben · Klick zum Bearbeiten"
                      >
                        <div className="text-[11px] font-medium truncate">{b.time} · {b.name}</div>
                        <div className="text-[10px] truncate opacity-80">{b.service}</div>
                        {b.status === "pending" && <div className="text-[9px] uppercase tracking-widest mt-0.5">Offen</div>}
                      </div>
                    );
                  })}
                </div>
              );
            });
          })}
        </div>
      </div>
    </div>
  );
};

export default function Kalender() {
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [dayRange, setDayRange] = useState(5); // 1 | 3 | 5
  const [anchorDate, setAnchorDate] = useState(() => new Date()); // used for 1/3 day views
  const [staff, setStaff] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const emptyForm = { name: "", email: "", phone: "", instagram: "", services: [], date: "", time: "10:00", duration_minutes: 60, notes: "", notify_client: true, staff_id: null };
  const [newForm, setNewForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const newTotal = newForm.services.reduce((sum, s) => sum + parsePrice(s.price), 0);
  const toggleService = (item) => setNewForm((f) => {
    const has = f.services.some((s) => s.name === item.name);
    return { ...f, services: has ? f.services.filter((s) => s.name !== item.name) : [...f.services, item] };
  });
  const [detail, setDetail] = useState(null); // clicked booking for edit/delete
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [settings, setSettings] = useState({ booking_window_weeks: 4, closures: [], opening_hours: {} });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [tab, setTab] = useState("termine"); // termine | mitarbeiter | ressourcen | kunden

  const load = useCallback(async (code) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings`, { pin: code });
      setBookings(data);
      setAuthed(true);
      sessionStorage.setItem("ma_pin", code);
      // load public settings alongside
      axios.get(`${API}/settings`).then(({ data: s }) => setSettings(s)).catch(() => {});
      axios.post(`${API}/admin/staff/list`, { pin: code }).then(({ data: st }) => setStaff((st || []).filter((s) => s.active))).catch(() => {});
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Falscher Code.");
      else toast.error("Fehler beim Laden.");
      setAuthed(false);
      sessionStorage.removeItem("ma_pin");
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const cleanedClosures = (settings.closures || [])
        .filter((c) => c.start)
        .map((c) => ({ start: c.start, end: c.end || c.start, note: (c.note || "").trim() }));
      const { data } = await axios.post(`${API}/admin/settings`, {
        pin,
        booking_window_weeks: settings.booking_window_weeks,
        closures: cleanedClosures,
        opening_hours: settings.opening_hours || {},
      });
      setSettings(data);
      toast.success("Einstellungen gespeichert.");
      setSettingsOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht speichern.");
    } finally {
      setSavingSettings(false);
    }
  };

  const addClosure = () => setSettings((s) => ({ ...s, closures: [...(s.closures || []), { start: "", end: "", note: "" }] }));
  const updateClosure = (i, patch) => setSettings((s) => ({ ...s, closures: s.closures.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeClosure = (i) => setSettings((s) => ({ ...s, closures: s.closures.filter((_, idx) => idx !== i) }));

  useEffect(() => {
    const saved = sessionStorage.getItem("ma_pin");
    if (saved) { setPin(saved); load(saved); }
  }, [load]);

  const submitPin = (e) => {
    e.preventDefault();
    if (pin.length !== 4) return toast.error("Bitte 4-stelligen Code eingeben.");
    load(pin);
  };

  const decide = async (b, action) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${b.id}/decision`, { token: b.token, action });
      setBookings((prev) => prev.map((x) => (x.id === b.id ? data : x)));
      toast.success(action === "annehmen" ? "Bestätigt – Kundin informiert." : "Abgelehnt – Kundin informiert.");
    } catch {
      toast.error("Aktion fehlgeschlagen.");
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newForm.name || !newForm.email || newForm.services.length === 0 || !newForm.date || !newForm.time) {
      toast.error("Bitte Name, E-Mail, mindestens eine Leistung, Datum & Uhrzeit ausfüllen.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        pin,
        name: newForm.name,
        email: newForm.email,
        phone: newForm.phone,
        instagram: newForm.instagram,
        service: newForm.services.map((s) => s.name).join(", "),
        price: formatEUR(newTotal),
        date: newForm.date,
        time: newForm.time,
        duration_minutes: newForm.duration_minutes,
        notes: newForm.notes,
        notify_client: true,
        staff_id: newForm.staff_id || null,
      };
      const { data } = await axios.post(`${API}/admin/bookings/create`, payload);
      setBookings((prev) => [data, ...prev]);
      toast.success("Termin eingetragen – Kundin wurde per E-Mail informiert.");
      setNewForm(emptyForm);
      setCreateOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte Termin nicht anlegen.");
    } finally {
      setCreating(false);
    }
  };
  const upd = (k) => (e) => setNewForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  // Shore-like Week-View interactions
  const openEmptySlot = (date, time, staffId = null) => {
    setNewForm({ ...emptyForm, date, time, staff_id: staffId });
    setCreateOpen(true);
  };
  const openBookingDetail = (b) => {
    setDetail(b);
    setEditForm({
      name: b.name, email: b.email, phone: b.phone || "", instagram: b.instagram || "",
      service: b.service, price: b.price || "", date: b.date, time: b.time,
      duration_minutes: b.duration_minutes || 60, notes: b.notes || "", status: b.status,
      staff_id: b.staff_id || "",
    });
  };
  const closeDetail = () => { setDetail(null); setEditForm(null); };
  const updEdit = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const saveEdit = async () => {
    if (!detail || !editForm) return;
    setSavingEdit(true);
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${detail.id}`, { pin, ...editForm, notify_client: true });
      setBookings((prev) => prev.map((x) => (x.id === detail.id ? data : x)));
      toast.success("Termin aktualisiert – Kundin informiert.");
      closeDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte nicht speichern.");
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteBooking = async (notify) => {
    if (!detail) return;
    if (!window.confirm(notify ? "Termin löschen und Kundin per E-Mail informieren?" : "Termin wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${detail.id}`, { params: { pin, notify } });
      setBookings((prev) => prev.filter((x) => x.id !== detail.id));
      toast.success(notify ? "Termin gelöscht – Kundin informiert." : "Termin gelöscht.");
      closeDetail();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen.");
    }
  };

  // Conflict detection: does the target slot overlap an existing booking?
  const findConflict = (id, date, time, durationMin) => {
    const start = timeToMinutes(time);
    const end = start + (durationMin || 60);
    return bookings.find((b) => {
      if (b.id === id || b.status === "rejected" || b.date !== date) return false;
      const bs = timeToMinutes(b.time);
      const be = bs + (b.duration_minutes || 60);
      return start < be && bs < end;
    });
  };

  // Drag & Drop: reschedule a booking to a new (date, time, staff_id)
  const reschedule = async (id, date, time, staffId = undefined) => {
    const original = bookings.find((x) => x.id === id);
    if (!original) return;
    const staffChanged = staffId !== undefined && staffId !== (original.staff_id ?? null);
    if (original.date === date && original.time === time && !staffChanged) return;
    const conflict = findConflict(id, date, time, original.duration_minutes);
    if (conflict) {
      const ok = window.confirm(
        `⚠️ Konflikt: Um ${conflict.time} Uhr am ${conflict.date} liegt bereits „${conflict.name} – ${conflict.service}". Trotzdem verschieben?`
      );
      if (!ok) return;
    }
    const payload = { pin, date, time, notify_client: true, force: !!conflict };
    if (staffId !== undefined) payload.staff_id = staffId;
    // Optimistic update
    setBookings((prev) => prev.map((x) => (x.id === id ? { ...x, date, time, ...(staffId !== undefined ? { staff_id: staffId } : {}) } : x)));
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${id}`, payload);
      setBookings((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success(`Termin verschoben auf ${date} ${time} – Kundin informiert.`);
    } catch (err) {
      setBookings((prev) => prev.map((x) => (x.id === id ? original : x)));
      toast.error(err?.response?.data?.detail || "Verschieben fehlgeschlagen.");
    }
  };

  // group by date
  const groups = bookings.reduce((acc, b) => {
    (acc[b.date] = acc[b.date] || []).push(b);
    return acc;
  }, {});
  const dates = Object.keys(groups).sort();

  if (!authed) {
    return (
      <div data-testid="kalender-locked" className="min-h-screen flex items-center justify-center px-5">
        <motion.form onSubmit={submitPin} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-sm text-center">
          <div className="w-14 h-14 mx-auto border border-border flex items-center justify-center mb-6"><Lock strokeWidth={1.25} /></div>
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Privater Bereich</span>
          <h1 className="font-serif font-light text-4xl tracking-tight mt-3 mb-8">Terminkalender</h1>
          <Input
            data-testid="pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            inputMode="numeric"
            placeholder="••••"
            className="h-16 rounded-none bg-background border-border text-center text-3xl tracking-[0.6em] font-serif"
          />
          <button data-testid="pin-submit" type="submit" disabled={loading} className="mt-5 w-full bg-primary text-primary-foreground py-4 text-sm tracking-wide disabled:opacity-60">
            {loading ? "Prüfe…" : "Öffnen"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div data-testid="kalender-page" className="pt-32 pb-24 min-h-screen">
      <div className="mx-auto max-w-[1000px] px-5 md:px-10">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Privater Bereich</span>
            <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter mt-4">Terminkalender</h1>
            <p className="mt-2 text-xs text-muted-foreground">Klick auf einen freien Slot legt einen Termin an · Termine per Drag &amp; Drop verschieben · Klick auf einen Block öffnet die Details.</p>
          </div>
          {tab === "termine" && (
            <div className="flex items-center gap-3">
              <button data-testid="new-booking-btn" onClick={() => setCreateOpen(true)} className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm tracking-wide hover:opacity-90 transition-opacity">
                <Plus strokeWidth={1.75} size={16} /> Neuer Termin
              </button>
              <button data-testid="settings-btn" onClick={() => setSettingsOpen(true)} className="w-10 h-10 border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" title="Einstellungen">
                <SettingsIcon strokeWidth={1.5} size={16} />
              </button>
              <div className="flex border border-border">
                <button data-testid="view-agenda" onClick={() => setView("agenda")} className={`px-4 py-2 text-sm transition-colors ${view === "agenda" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"}`}>Agenda</button>
                <button data-testid="view-week" onClick={() => setView("week")} className={`px-4 py-2 text-sm transition-colors ${view === "week" ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"}`}>Woche</button>
              </div>
              <button data-testid="kalender-refresh" onClick={() => load(pin)} className="group flex items-center gap-2 text-sm text-foreground/70 hover:text-foreground">
                <RefreshCw size={16} className="group-hover:rotate-180 transition-transform duration-500" /> Aktualisieren
              </button>
            </div>
          )}
        </div>

        {/* Tab-Navigation */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-border" data-testid="kalender-tabs">
          {[
            { k: "termine", label: "Termine" },
            { k: "warteliste", label: "Warteliste" },
            { k: "academy", label: "Academy" },
            { k: "mitarbeiter", label: "Mitarbeiter" },
            { k: "ressourcen", label: "Ressourcen" },
            { k: "kunden", label: "Kunden" },
          ].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`px-4 py-2.5 text-sm tracking-wide -mb-px border-b-2 transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-foreground/60 hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "mitarbeiter" && <div className="mt-10"><StaffTab pin={pin} /></div>}
        {tab === "ressourcen" && <div className="mt-10"><ResourcesTab pin={pin} /></div>}
        {tab === "kunden" && <div className="mt-10"><CustomersTab pin={pin} /></div>}
        {tab === "warteliste" && <div className="mt-10"><WaitlistTab pin={pin} /></div>}
        {tab === "academy" && <div className="mt-10"><AcademyTab pin={pin} /></div>}

        {tab === "termine" && <>

        {view === "week" && <WeekView anchorDate={anchorDate} setAnchorDate={setAnchorDate} dayRange={dayRange} setDayRange={setDayRange} staff={staff} bookings={bookings} closures={settings.closures || []} onEmptySlot={openEmptySlot} onBookingClick={openBookingDetail} onReschedule={reschedule} />}

        <div className={`mt-12 space-y-10 ${view === "week" ? "hidden" : ""}`}>
          {dates.length === 0 && (
            <div data-testid="kalender-empty" className="border border-dashed border-border py-20 text-center text-foreground/60">
              <CalIcon className="mx-auto mb-4 text-primary/50" strokeWidth={1} size={40} />
              Noch keine Terminanfragen.
            </div>
          )}

          {dates.map((d) => (
            <div key={d}>
              <h2 className="font-serif text-2xl mb-4 pb-2 border-b border-border">{fmtDate(d)}</h2>
              <div className="space-y-3">
                {groups[d].sort((a, b) => a.time.localeCompare(b.time)).map((b) => {
                  const st = STATUS[b.status] || STATUS.pending;
                  return (
                    <div key={b.id} data-testid="kalender-booking" className="border border-border p-5 flex flex-col md:flex-row md:items-center gap-4">
                      <div className="font-serif text-2xl w-20 shrink-0">{b.time}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{b.name}</span>
                          <span className={`text-[11px] uppercase tracking-wide px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                        </div>
                        <p className="text-sm text-foreground/70 mt-1">{b.service}{b.price ? ` · ${b.price}` : ""}</p>
                        <p className="text-xs text-muted-foreground mt-1">{b.email} · {b.phone}{b.instagram ? ` · ${b.instagram}` : ""}{b.notes ? ` · ${b.notes}` : ""}</p>
                        {b.status === "rejected" && b.reject_reason ? <p className="text-xs text-foreground/50 mt-1">Grund: {b.reject_reason}</p> : null}
                      </div>
                      {b.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <button data-testid={`kal-accept-${b.id}`} onClick={() => decide(b, "annehmen")} className="flex items-center gap-1.5 bg-primary text-primary-foreground px-4 py-2.5 text-sm hover:opacity-90">
                            <Check size={15} /> Annehmen
                          </button>
                          <button data-testid={`kal-reject-${b.id}`} onClick={() => decide(b, "ablehnen")} className="flex items-center gap-1.5 border border-foreground px-4 py-2.5 text-sm hover:bg-foreground hover:text-background transition-colors">
                            <X size={15} /> Ablehnen
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </>}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-booking-dialog" className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Termin manuell eintragen</DialogTitle>
            <p className="text-xs text-muted-foreground pt-1">Wird sofort als bestätigt gespeichert. Die Kundin bekommt automatisch eine E-Mail und 2 Tage vor dem Termin eine Erinnerung.</p>
          </DialogHeader>
          <form onSubmit={submitNew} className="grid grid-cols-2 gap-3 pt-2">
            <Input data-testid="nb-name" placeholder="Name *" value={newForm.name} onChange={upd("name")} className="col-span-2 rounded-none" />
            <Input data-testid="nb-email" placeholder="E-Mail *" type="email" value={newForm.email} onChange={upd("email")} className="col-span-2 rounded-none" />
            <Input data-testid="nb-phone" placeholder="Telefon" value={newForm.phone} onChange={upd("phone")} className="rounded-none" />
            <Input data-testid="nb-instagram" placeholder="Instagram" value={newForm.instagram} onChange={upd("instagram")} className="rounded-none" />
            <Input data-testid="nb-service-search" placeholder="Suche in Leistungen…" disabled className="col-span-2 rounded-none hidden" value="" onChange={() => {}} />
            <div className="col-span-2 border border-border max-h-[240px] min-h-[240px] overflow-y-auto" data-testid="nb-services" style={{ WebkitOverflowScrolling: "touch" }}>
              {BOOKABLE_GROUPS.map((group) => (
                <div key={group.group} className="border-b border-border last:border-b-0">
                  <div className="bg-muted px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{group.group}</div>
                  {group.items.map((it) => {
                    const active = newForm.services.some((s) => s.name === it.name);
                    return (
                      <label key={it.name} data-testid={`svc-${it.name}`} className={`flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-primary/5 ${active ? "bg-primary/10" : ""}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <input type="checkbox" checked={active} onChange={() => toggleService(it)} className="accent-primary shrink-0" />
                          <span className="truncate">{it.name}</span>
                        </span>
                        <span className="text-xs text-foreground/60 tabular-nums shrink-0">{it.price}</span>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="col-span-2 flex items-center justify-between bg-primary/8 px-3 py-2.5 text-sm">
              <span className="text-foreground/70">{newForm.services.length} Leistung{newForm.services.length === 1 ? "" : "en"} ausgewählt</span>
              <span data-testid="nb-total" className="font-serif text-lg text-primary">Gesamt: {formatEUR(newTotal)}</span>
            </div>
            <Input data-testid="nb-date" type="date" value={newForm.date} onChange={upd("date")} className="rounded-none" />
            <Input data-testid="nb-time" type="time" value={newForm.time} onChange={upd("time")} className="rounded-none" />
            <div className="col-span-2 flex items-center gap-2">
              <label className="text-sm text-foreground/70 shrink-0">Dauer:</label>
              <select data-testid="nb-duration" value={newForm.duration_minutes} onChange={(e) => setNewForm((f) => ({ ...f, duration_minutes: Number(e.target.value) }))} className="flex-1 h-10 rounded-none bg-background border border-border px-3 text-sm">
                <option value={30}>30 Minuten</option>
                <option value={45}>45 Minuten</option>
                <option value={60}>1 Stunde</option>
                <option value={90}>1,5 Stunden</option>
                <option value={120}>2 Stunden</option>
                <option value={150}>2,5 Stunden</option>
                <option value={180}>3 Stunden</option>
              </select>
            </div>
            {staff.length > 0 && (
              <div className="col-span-2 flex items-center gap-2">
                <label className="text-sm text-foreground/70 shrink-0">Mitarbeiter:</label>
                <select data-testid="nb-staff" value={newForm.staff_id || ""} onChange={(e) => setNewForm((f) => ({ ...f, staff_id: e.target.value || null }))} className="flex-1 h-10 rounded-none bg-background border border-border px-3 text-sm">
                  <option value="">— nicht zugeordnet —</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <Textarea data-testid="nb-notes" placeholder="Notizen (optional)" value={newForm.notes} onChange={upd("notes")} className="col-span-2 rounded-none min-h-[80px]" />
            <p className="col-span-2 flex items-center gap-2 text-xs text-primary bg-primary/8 px-3 py-2.5">
              <span aria-hidden>✉️</span>
              Die Kundin wird automatisch per E-Mail über den Termin informiert (inkl. AGB-Hinweis zur 48h-Stornierung).
            </p>
            <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground">Abbrechen</button>
              <button data-testid="nb-submit" type="submit" disabled={creating} className="bg-primary text-primary-foreground px-6 py-2.5 text-sm tracking-wide disabled:opacity-60">
                {creating ? "Speichere…" : "Termin anlegen"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Termin-Detail / Bearbeiten / Löschen */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent data-testid="edit-booking-dialog" className="max-w-lg rounded-none">
          {editForm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light">Termin bearbeiten</DialogTitle>
                <p className="text-xs text-muted-foreground pt-1">Status: <span className="text-primary uppercase tracking-wide">{editForm.status}</span></p>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Input data-testid="ed-name" placeholder="Name" value={editForm.name} onChange={updEdit("name")} className="col-span-2 rounded-none" />
                <Input data-testid="ed-email" placeholder="E-Mail" type="email" value={editForm.email} onChange={updEdit("email")} className="col-span-2 rounded-none" />
                <Input data-testid="ed-phone" placeholder="Telefon" value={editForm.phone} onChange={updEdit("phone")} className="rounded-none" />
                <Input data-testid="ed-instagram" placeholder="Instagram" value={editForm.instagram} onChange={updEdit("instagram")} className="rounded-none" />
                <Input data-testid="ed-service" placeholder="Leistung" value={editForm.service} onChange={updEdit("service")} className="col-span-2 rounded-none" />
                <Input data-testid="ed-price" placeholder="Preis" value={editForm.price} onChange={updEdit("price")} className="col-span-2 rounded-none" />
                <Input data-testid="ed-date" type="date" value={editForm.date} onChange={updEdit("date")} className="rounded-none" />
                <Input data-testid="ed-time" type="time" value={editForm.time} onChange={updEdit("time")} className="rounded-none" />
                <div className="col-span-2 flex items-center gap-2">
                  <label className="text-sm text-foreground/70 shrink-0">Dauer:</label>
                  <select data-testid="ed-duration" value={editForm.duration_minutes} onChange={(e) => setEditForm((f) => ({ ...f, duration_minutes: Number(e.target.value) }))} className="flex-1 h-10 rounded-none bg-background border border-border px-3 text-sm">
                    {[30, 45, 60, 90, 120, 150, 180].map((d) => (
                      <option key={d} value={d}>{d < 60 ? `${d} Minuten` : `${d / 60} Stunde${d > 60 ? "n" : ""}`}</option>
                    ))}
                  </select>
                </div>
                {staff.length > 0 && (
                  <div className="col-span-2 flex items-center gap-2">
                    <label className="text-sm text-foreground/70 shrink-0">Mitarbeiter:</label>
                    <select data-testid="ed-staff" value={editForm.staff_id || ""} onChange={(e) => setEditForm((f) => ({ ...f, staff_id: e.target.value || null }))} className="flex-1 h-10 rounded-none bg-background border border-border px-3 text-sm">
                      <option value="">— nicht zugeordnet —</option>
                      {staff.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <Textarea data-testid="ed-notes" placeholder="Notizen" value={editForm.notes} onChange={updEdit("notes")} className="col-span-2 rounded-none min-h-[70px]" />
                <div className="col-span-2 flex items-center gap-3 pt-1">
                  <label className="text-sm text-foreground/70">Status:</label>
                  {["pending", "accepted", "rejected"].map((s) => (
                    <button key={s} type="button" data-testid={`ed-status-${s}`} onClick={() => setEditForm((f) => ({ ...f, status: s }))} className={`text-xs px-3 py-1 border ${editForm.status === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:border-primary"}`}>
                      {s === "pending" ? "Offen" : s === "accepted" ? "Bestätigt" : "Abgelehnt"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 pt-4 border-t border-border mt-4">
                <div className="flex gap-2">
                  <button type="button" data-testid="ed-delete" onClick={() => deleteBooking(false)} className="text-sm text-red-700 hover:underline">
                    Löschen
                  </button>
                  <button type="button" data-testid="ed-delete-notify" onClick={() => deleteBooking(true)} className="text-sm text-red-700 hover:underline">
                    Löschen &amp; Kundin informieren
                  </button>
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={closeDetail} className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground">Abbrechen</button>
                  <button type="button" data-testid="ed-save" onClick={saveEdit} disabled={savingEdit} className="bg-primary text-primary-foreground px-6 py-2.5 text-sm tracking-wide disabled:opacity-60">
                    {savingEdit ? "Speichere…" : "Speichern"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Einstellungen: Buchungsfenster + Studio-Schließungen */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="settings-dialog" className="max-w-xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="pt-2 space-y-6">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Buchungsfenster</label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  data-testid="settings-weeks"
                  type="number"
                  min={1}
                  max={52}
                  value={settings.booking_window_weeks}
                  onChange={(e) => setSettings((s) => ({ ...s, booking_window_weeks: Math.max(1, Math.min(52, Number(e.target.value) || 1)) }))}
                  className="w-24 rounded-none text-center"
                />
                <span className="text-sm text-foreground/70">Wochen im Voraus</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                Kundinnen sehen auf der Buchungsseite nur Termine bis zu diesem Zeitraum. Deine Kalenderansicht bleibt davon unberührt.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Studio-Schließungen (Urlaub / Feiertage)</label>
                <button type="button" data-testid="closure-add" onClick={addClosure} className="flex items-center gap-1 border border-primary text-primary px-3 py-1 text-xs hover:bg-primary hover:text-primary-foreground transition-colors">
                  <Plus size={12} /> Zeitraum hinzufügen
                </button>
              </div>
              <div className="space-y-2">
                {(settings.closures || []).map((c, i) => (
                  <div key={i} data-testid={`closure-row-${i}`} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-center border border-border p-2">
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Von<Input type="date" value={c.start} onChange={(e) => updateClosure(i, { start: e.target.value })} className="mt-1 rounded-none" /></label>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bis<Input type="date" value={c.end} onChange={(e) => updateClosure(i, { end: e.target.value })} className="mt-1 rounded-none" /></label>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Notiz<Input placeholder="z.B. Urlaub, Fortbildung" value={c.note} onChange={(e) => updateClosure(i, { note: e.target.value })} className="mt-1 rounded-none" /></label>
                    <button type="button" onClick={() => removeClosure(i)} className="text-foreground/40 hover:text-red-600 pt-4"><X size={16} /></button>
                  </div>
                ))}
                {!(settings.closures || []).length && (
                  <p className="text-xs text-muted-foreground border border-dashed border-border p-4 text-center">Keine Schließungen eingetragen.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2">
                An diesen Tagen ist die öffentliche Buchung gesperrt und der Kalender zeigt die Tage als geschlossen.
              </p>
            </div>

            {/* Öffnungszeiten */}
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Öffnungszeiten</label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Zeigt sich Kundinnen auf der Startseite und im Footer. Standard: Mo–Fr 09–17, Wochenende geschlossen.</p>
              <div className="border border-border divide-y divide-border">
                {[
                  { k: "mo", label: "Montag" },
                  { k: "di", label: "Dienstag" },
                  { k: "mi", label: "Mittwoch" },
                  { k: "do", label: "Donnerstag" },
                  { k: "fr", label: "Freitag" },
                  { k: "sa", label: "Samstag" },
                  { k: "so", label: "Sonntag" },
                ].map(({ k, label }) => {
                  const oh = settings.opening_hours || {};
                  const dflt = { mo: { o: "09:00", c: "17:00", cl: false }, di: { o: "09:00", c: "17:00", cl: false }, mi: { o: "09:00", c: "17:00", cl: false }, do: { o: "09:00", c: "17:00", cl: false }, fr: { o: "09:00", c: "17:00", cl: false }, sa: { o: "09:00", c: "17:00", cl: true }, so: { o: "09:00", c: "17:00", cl: true } }[k];
                  const row = oh[k] || { open: dflt.o, close: dflt.c, closed: dflt.cl };
                  const set = (patch) => setSettings((s) => ({
                    ...s,
                    opening_hours: { ...(s.opening_hours || {}), [k]: { open: row.open || dflt.o, close: row.close || dflt.c, closed: !!row.closed, ...patch } },
                  }));
                  return (
                    <div key={k} className="flex items-center gap-3 p-2.5 flex-wrap">
                      <div className="w-24 text-sm">{label}</div>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input type="checkbox" checked={!!row.closed} onChange={(e) => set({ closed: e.target.checked })} data-testid={`hours-${k}-closed`} /> geschlossen
                      </label>
                      <div className="flex items-center gap-2">
                        <input type="time" value={row.open || "09:00"} disabled={!!row.closed} onChange={(e) => set({ open: e.target.value })} data-testid={`hours-${k}-open`} className="border border-border px-2 py-1 text-sm disabled:opacity-40" />
                        <span className="text-foreground/40">–</span>
                        <input type="time" value={row.close || "17:00"} disabled={!!row.closed} onChange={(e) => set({ close: e.target.value })} data-testid={`hours-${k}-close`} className="border border-border px-2 py-1 text-sm disabled:opacity-40" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-6 border-t border-border mt-4">
            <button type="button" onClick={() => setSettingsOpen(false)} className="px-4 py-2 text-sm text-foreground/70 hover:text-foreground">Abbrechen</button>
            <button type="button" data-testid="settings-save" onClick={saveSettings} disabled={savingSettings} className="bg-primary text-primary-foreground px-6 py-2.5 text-sm tracking-wide disabled:opacity-60">
              {savingSettings ? "Speichere…" : "Speichern"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
